import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["siteSelect", "layoutSelect", "layoutContainer", "tabList", "tabContent", "addButton"]
  static values = {
    storyId: Number,
    sites: Array  // Array of { id, title, layouts: [{ id, name }] }
  }

  connect() {
    this.updateLayoutOptions()
    this.updateAddButtonState()
  }

  // Called when site selection changes
  siteChanged(event) {
    this.updateLayoutOptions()
    this.updateAddButtonState()
  }

  // Called when layout selection changes
  layoutChanged(event) {
    this.updateAddButtonState()
  }

  // Update the layout dropdown based on selected site
  updateLayoutOptions() {
    if (!this.hasSiteSelectTarget || !this.hasLayoutSelectTarget) return

    const siteId = this.siteSelectTarget.value
    const layoutSelect = this.layoutSelectTarget

    // Clear existing options
    layoutSelect.innerHTML = '<option value="">Select layout...</option>'

    if (!siteId) {
      this.layoutContainerTarget.classList.add('d-none')
      return
    }

    // Find the selected site's layouts
    const site = this.sitesValue.find(s => s.id === parseInt(siteId))
    if (site && site.layouts) {
      site.layouts.forEach(layout => {
        const option = document.createElement('option')
        option.value = layout.id
        option.textContent = layout.name
        layoutSelect.appendChild(option)
      })
      this.layoutContainerTarget.classList.remove('d-none')
    }
  }

  // Enable/disable add button based on selections
  updateAddButtonState() {
    if (!this.hasAddButtonTarget) return

    const hasSite = this.hasSiteSelectTarget && this.siteSelectTarget.value
    const hasLayout = this.hasLayoutSelectTarget && this.layoutSelectTarget.value

    this.addButtonTarget.disabled = !(hasSite && hasLayout)
  }

  // Add a new site/layout to the story via POST request
  async add(event) {
    event.preventDefault()

    if (!this.hasSiteSelectTarget || !this.hasLayoutSelectTarget) return

    const siteId = this.siteSelectTarget.value
    const layoutId = this.layoutSelectTarget.value

    if (!siteId || !layoutId) {
      alert('Please select both a site and a layout')
      return
    }

    // Disable the button and show loading state
    this.addButtonTarget.disabled = true
    const originalContent = this.addButtonTarget.innerHTML
    this.addButtonTarget.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i> Adding...'

    try {
      const response = await fetch(`/stories/${this.storyIdValue}/site_stories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          site_story: {
            sites_layouts_id: layoutId
          }
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Log debug info if available
        if (data.debug) {
          console.log('=== Site Story Debug Info ===')
          console.log(`Layout has ${data.debug.layout_elements} layout elements`)
          console.log(`Created ${data.debug.story_elements_created} story elements`)
          console.log(`${data.debug.story_category_elements} have category "story"`)
          if (data.debug.copy_error) {
            console.error('Error during copy:', data.debug.copy_error)
          }
        }

        // Reload the page to show the new site story with its elements
        window.location.reload()
      } else {
        // Show error message
        const errorMessage = data.errors ? data.errors.join(', ') : (data.error || 'Failed to add site')
        alert(errorMessage)

        // Reset button state
        this.addButtonTarget.innerHTML = originalContent
        this.addButtonTarget.disabled = false
      }
    } catch (error) {
      console.error('Error adding site:', error)
      alert('An error occurred while adding the site. Please try again.')

      // Reset button state
      this.addButtonTarget.innerHTML = originalContent
      this.addButtonTarget.disabled = false
    }
  }

  // Remove a site from the story via DELETE request with confirmation
  async remove(event) {
    event.preventDefault()

    const button = event.target.closest('button')
    if (!button) return

    const siteStoryId = button.dataset.siteStoryId
    const siteTitle = button.dataset.siteTitle || 'this site'

    if (!siteStoryId) {
      console.error('No site story ID found')
      return
    }

    // Show SweetAlert confirmation
    const result = await Swal.fire({
      title: 'Remove from Story?',
      text: `Are you sure you want to remove "${siteTitle}" from this story? This will delete all configured elements for this site.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove it',
      cancelButtonText: 'Cancel',
      customClass: {
        title: 'color-fanword-black font-fanword-regular',
        confirmButton: 'btn-sm px-4 py-2 text-decoration-none bg-danger text-white font-fanword-extra-bold fanword-no-focus',
        cancelButton: 'btn-sm px-4 py-2 text-decoration-none bg-fanword-background color-fanword-text font-fanword-extra-bold fanword-no-focus'
      }
    })

    if (!result.isConfirmed) {
      return
    }

    // Disable the button and show loading state
    button.disabled = true
    const originalContent = button.innerHTML
    button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'

    try {
      const response = await fetch(`/stories/${this.storyIdValue}/site_stories/${siteStoryId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Reload the page to show the updated state
        window.location.reload()
      } else {
        // Show error message
        const errorMessage = data.errors ? data.errors.join(', ') : (data.error || 'Failed to remove site')
        Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error'
        })

        // Reset button state
        button.innerHTML = originalContent
        button.disabled = false
      }
    } catch (error) {
      console.error('Error removing site:', error)
      Swal.fire({
        title: 'Error',
        text: 'An error occurred while removing the site. Please try again.',
        icon: 'error'
      })

      // Reset button state
      button.innerHTML = originalContent
      button.disabled = false
    }
  }
}
